import styled from 'styled-components';

import Select from 'shared/atoms/Select';
import Icon, { type IconProps } from 'shared/atoms/Icon';
import { DOMAIN_LOCALES, LANGUAGE_NAMES } from 'shared/const';

import EnglishIcon from 'shared/assets/en.svg?react';
import NorwegianIcon from 'shared/assets/no.svg?react';
import SpanishIcon from 'shared/assets/es.svg?react';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

interface LanguagePickerProps {
  currentLocale: string;
  domain: string;
  onChange: (locale: string) => void;
}

const getLanguageIcon = (locale: string) => {
  // TODO: Remove type assertions (as IconProps['svgContent']) once the Icon component
  // or SVG import pipeline is migrated to properly type SVG imports.
  // Currently needed because SVG imports return FunctionComponent<SVGProps<SVGSVGElement>>
  // but Icon expects FunctionComponent<SvgContentProps> with stricter prop types.
  switch (locale) {
    case 'no':
      return (
        <Icon
          svgContent={NorwegianIcon as IconProps['svgContent']}
          viewBox="0 0 32 16"
        />
      );
    case 'en':
      return (
        <Icon
          svgContent={EnglishIcon as IconProps['svgContent']}
          viewBox="0 0 32 16"
        />
      );
    case 'es':
      return (
        <Icon
          svgContent={SpanishIcon as IconProps['svgContent']}
          viewBox="0 0 32 16"
        />
      );
    default:
      return null;
  }
};

const LanguagePicker = ({
  currentLocale,
  domain,
  onChange,
}: LanguagePickerProps) => {
  const availableLocales =
    DOMAIN_LOCALES[domain as keyof typeof DOMAIN_LOCALES] || DOMAIN_LOCALES.no;

  const localeOptions = availableLocales.map((locale) => ({
    value: locale,
    key: locale,
    label: LANGUAGE_NAMES[locale as keyof typeof LANGUAGE_NAMES],
    icon: getLanguageIcon(locale),
  }));

  const handleChange = ({
    value,
  }: {
    value: string | number | boolean | (string | number | boolean)[];
  }) => {
    onChange(String(value));
  };

  return (
    <Wrapper>
      <Select
        fullWidth={false}
        iconOnly
        id="language-picker"
        items={localeOptions}
        name="language"
        onChange={handleChange}
        required
        theme="dark"
        value={currentLocale}
      />
    </Wrapper>
  );
};

export default LanguagePicker;
